import axios from 'axios';

/**
 * API base. Previously hardcoded in AppUrl.jsx, which meant pointing the site
 * at a different backend required editing source and rebuilding.
 */
export const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'https://backend.chickrah.com/api';

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: { Accept: 'application/json' },
});

/**
 * The CMS stores image paths as ABSOLUTE urls — its controllers save
 * asset('upload/...'), which bakes whatever APP_URL was set at insert time
 * into the row. Rows inserted from a dev machine therefore carry
 * http://soya.test or http://127.0.0.1:8000 and 404 in production.
 *
 * Rewrites any known-bad host onto the real backend. The proper fix is to
 * store relative paths and prepend at read time, which is a backend change
 * plus a data migration.
 */
const BAD_HOSTS = /^https?:\/\/(soya\.test|127\.0\.0\.1(:\d+)?|localhost(:\d+)?)/i;

export function absolutize(url) {
  if (!url || typeof url !== 'string') return null;
  const origin = API_BASE.replace(/\/api\/?$/, '');
  if (BAD_HOSTS.test(url)) return url.replace(BAD_HOSTS, origin);
  if (url.startsWith('http')) return url;
  return `${origin}/${url.replace(/^\/+/, '')}`;
}

export const ENDPOINTS = {
  testimonials: '/testimonials',
  companies: '/company',
  demoRequest: '/demo-request',
};

const YT_PATH = /(?:youtube\.com\/(?:embed|v|shorts)\/|youtu\.be\/)([\w-]{11})/;
const YT_QUERY = /[?&]v=([\w-]{11})/;

/**
 * Turns a stored YouTube url into a privacy-friendly embed url, plus the
 * poster thumbnail.
 *
 * The CMS stores whatever was pasted — a watch link, a share link, or an
 * embed url already — so all three forms are handled.
 *
 * youtube-nocookie.com avoids setting tracking cookies on visitors who never
 * press play, which matters because the player is only loaded on click.
 */
export function parseYouTube(url) {
  if (!url || typeof url !== 'string') return null;

  const id = url.match(YT_PATH)?.[1] ?? url.match(YT_QUERY)?.[1];
  if (!id) return null;

  return {
    id,
    // autoplay is safe here: the iframe is only mounted after a real click.
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`,
    posterUrl: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
    posterFallbackUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
  };
}

/** Gallery endpoints powering the "Chickrah in action" rail. */
ENDPOINTS.projects = '/projectall';
ENDPOINTS.iotImages = '/iotimages';
